import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  EmailAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  updatePassword,
  User,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import "./firebase.config";
import {
  IAuthProvider,
  AuthUser,
  AuthResult,
  AuthError,
  UserProfile,
  SignUpResult,
} from "../../types";

const ADMIN_COLLECTION = "admin";
const TREASURY_ID_FIELD = "treasuryId";
const EMAIL_FIELD = "email";

function mapFirebaseUser(user: User): AuthUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    emailVerified: user.emailVerified,
  };
}

function mapFirebaseError(error: any): AuthError {
  return {
    code: error.code ?? "auth/unknown",
    message: error.message ?? "An unknown error occurred",
  };
}

class FirebaseAuthProvider implements IAuthProvider {
  private auth = getAuth();
  private db = getFirestore();

  async signIn(email: string, password: string): Promise<AuthResult> {
    try {
      const credential = await signInWithEmailAndPassword(
        this.auth,
        email,
        password,
      );
      return { user: mapFirebaseUser(credential.user) };
    } catch (error: any) {
      const mapped = mapFirebaseError(error);
      throw mapped;
    }
  }

  async signUp(email: string, password: string): Promise<SignUpResult> {
    try {
      const credential = await createUserWithEmailAndPassword(
        this.auth,
        email,
        password,
      );
      const uid = credential.user.uid;
      // Sign out immediately — the session will be established on login.
      // Password never leaves the browser.
      await firebaseSignOut(this.auth);
      return { uid };
    } catch (error: any) {
      throw mapFirebaseError(error);
    }
  }

  async signOut(): Promise<void> {
    await firebaseSignOut(this.auth);
  }

  async signInWithCustomToken(
    token: string,
    tenantId?: string | null,
  ): Promise<AuthResult> {
    try {
      // Must be set BEFORE redeeming: a custom token minted in a tenant is only
      // redeemable by a client pointed at that same tenant. null = default pool.
      this.auth.tenantId = tenantId ?? null;
      const credential = await firebaseSignInWithCustomToken(this.auth, token);
      return { user: mapFirebaseUser(credential.user) };
    } catch (error: any) {
      throw mapFirebaseError(error);
    }
  }

  async setPassword(password: string): Promise<void> {
    try {
      const user = this.auth.currentUser;
      if (!user) throw new Error("No authenticated user found.");
      await updatePassword(user, password);
      await firebaseSignOut(this.auth);
    } catch (error: any) {
      throw mapFirebaseError(error);
    }
  }

  getCurrentUser(): AuthUser | null {
    const user = this.auth.currentUser;
    return user ? mapFirebaseUser(user) : null;
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return firebaseOnAuthStateChanged(this.auth, (user) => {
      // auth.tenantId does NOT survive a page reload, but the restored user carries
      // its own. Re-applying it here keeps later token refreshes, reauthentication
      // and password changes on the pool that actually owns this identity.
      if (user) this.auth.tenantId = user.tenantId ?? null;
      callback(user ? mapFirebaseUser(user) : null);
    });
  }

  async getEmailByTreasuryId(treasuryId: string): Promise<string | null> {
    console.log(`getEmailByTreasuryId`);
    const usersRef = collection(this.db, ADMIN_COLLECTION);
    const q = query(usersRef, where(TREASURY_ID_FIELD, "==", treasuryId));
    const snapshot = await getDocs(q);
    console.log(`getEmailByTreasuryId snapshot: `, snapshot);
    if (snapshot.empty) return null;
    console.log(`getEmailByTreasuryId snapshot.docs: `, snapshot.docs);
    return snapshot.docs[0].data()[EMAIL_FIELD] ?? null;
  }

  async getUserProfile(uid: string): Promise<UserProfile> {
    const snap = await getDoc(doc(this.db, ADMIN_COLLECTION, uid));
    if (!snap.exists()) return { mustChangePassword: false, treasuryId: null };
    const data = snap.data();
    return {
      mustChangePassword: data.mustChangePassword === true,
      treasuryId: (data[TREASURY_ID_FIELD] as string) ?? null,
    };
  }

  async getMustChangePassword(uid: string): Promise<boolean> {
    const profile = await this.getUserProfile(uid);
    return profile.mustChangePassword;
  }

  /**
   * The signed-in user's email is the credential's identifier. AuthUser.email is populated from the
   * Firebase user, so it is available even though the profile shape carries none.
   */
  async sendPasswordReset(email: string, continueUrl?: string): Promise<void> {
    try {
      await sendPasswordResetEmail(
        this.auth,
        email,
        continueUrl ? { url: continueUrl } : undefined,
      );
    } catch (error: any) {
      throw mapFirebaseError(error);
    }
  }

  async reauthenticate(currentPassword: string): Promise<void> {
    try {
      const user = this.auth.currentUser;
      if (!user?.email) throw new Error("No authenticated user found.");
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
    } catch (error: any) {
      throw mapFirebaseError(error);
    }
  }

  // Wrapped like every sibling: unwrapped, a Firebase failure reaches parseApiError's last branch
  // and the user reads the literal "Firebase: Error (auth/...)".
  async changePassword(newPassword: string): Promise<void> {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        throw new Error("User is not found");
      }
      await updatePassword(user, newPassword);
      await updateDoc(doc(this.db, ADMIN_COLLECTION, user.uid), {
        mustChangePassword: false,
      });
    } catch (error: any) {
      throw mapFirebaseError(error);
    }
  }

  async getToken(): Promise<string | null> {
    try {
      return (await this.auth.currentUser?.getIdToken()) ?? null;
    } catch {
      // Refresh token revoked or user disabled — treat as unauthenticated
      return null;
    }
  }
}

export default FirebaseAuthProvider;
